
import React, { useState, useEffect, Fragment, useMemo } from 'react'

import { useTeams } from '@/Hooks/UseTeam'
import useTranslation from '@/Hooks/UseTranslation'
import UserAvatar from '@/Components/UserAvatar/UserAvatar'
import OptionsDialog from '@/Components/OptionsDialog/OptionsDialog'
import { useAuth } from '@/Hooks/UseAuth'
import TeamSelector from '@/Components/TeamSelector/TeamSelector'
import Swal from 'sweetalert2'
import { useHistory } from 'react-router-dom'
import { useProject } from '@/Hooks/UseProject'
import { unassignInternalProfile } from '@/Services/web/InternalProfileService'

const InternalProfiles = () => {

  const { t } = useTranslation()

  const history = useHistory()

  const { project } = useProject()

  const { ability } = useAuth()

  const {
    teams,
    teamSelected,
    moveProfileToTeam,
    removeProfileFromTeam,
  } = useTeams()

  const [profile, setProfile] = useState<any>(undefined)
  const [showOptions, setShowOptions] = useState(false)
  const [ showTeamSelector, setShowTeamSelector ] = useState(false)

  const options = useMemo(() => {

    const options: any[] = []

    if (!profile) return options

    if (ability.can('move', 'TeamMember')) {

      options.push({
        icon: <span className="icon-cambiar_equipo" />,
        label: t('TEAMS:ContextMenuSwapTeamOption'),
        onClick: () => setShowTeamSelector(true)
      })
    }

    if (ability.can('remove', 'TeamMember')) {

      options.push({
        icon: <span className="icon-eliminar" />,
        color: 'var(--color-danger)',
        label: t('TEAMS:ContextMenuRemoveTeamMemberOption'),
        onClick: () => removeProfile(profile._id)
      })
    }

    return options
  }, [ profile, showOptions ])

  const handleShowOptions = (profile: any) => {

    setProfile(profile)
    setShowOptions(true)
  }

  const moveToTeam = async (toTeam: any) => {

    await moveProfileToTeam(profile, toTeam)

    history.replace(`/project/${project._id}/teams/${toTeam._id}`);

    setProfile(undefined)
    setShowTeamSelector(false)
  }

  const removeProfile = async (profile_id: string) => {
    const result = await Swal.fire({
      title: t('TEAMS:RemoveUserAlertTitle'),
      text: t('TEAMS:RemoveUserAlertContent'),
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: t('TEAMS:RemoveUserAlertCancelButton'),
      confirmButtonText: t('TEAMS:RemoveUserAlertConfirmButton'),
      width: "500px"
    })

    if (result.isConfirmed) {

      await unassignInternalProfile({ team_id: teamSelected._id, internalProfile_id: profile_id })

      removeProfileFromTeam(profile_id, teamSelected._id)
    }
  }

  if (!teamSelected.internalProfiles || teamSelected.internalProfiles.length === 0) return null

  return (
    <Fragment>
      <div className='members-section'>
        <h5>{t('TEAMS:InternalProfilesTitle')}</h5>
        <div className="members-content-list">
          {teamSelected.internalProfiles.map((profile: any, i: number) => (
            <div key={`${i}-${profile._id}`} className="members-content-item">
              <UserAvatar user={profile} />
              <div className="member-texts">
                <div className='member-name'>
                  {profile.name} {profile.surname}
                </div>
                <div className='member-email'>
                  {profile.email}
                </div>
              </div>
              <div className="profile-responsible">
                <div className="title">
                  {t('INTERNAL_PROFILES:Responsible')}
                </div>
                <div>
                  <UserAvatar user={profile.responsible} size='sm' />{' '}
                  {profile.responsible.name} {profile.responsible.surname}
                </div>
              </div>
              <div className="member-actions">
                  <button
                    className="btn btn-light mr-1"
                    onClick={() => handleShowOptions(profile)}
                  >
                    <i className="fas fa-fw fa-ellipsis-v"></i>
                  </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <OptionsDialog
        show={showOptions}
        title={`${profile?.name} ${profile?.surname}`}
        closeButton
        onHide={() => {

          setShowOptions(false)
        }}
        options={options}
      />

      <TeamSelector
        show={showTeamSelector}
        teams={teams}
        teamSelected={teamSelected}
        onSelectTeam={(toTeam: any) => moveToTeam(toTeam)}
        onHide={() =>
          setShowTeamSelector(false)
        }
      />
    </Fragment>
  )
}

export default InternalProfiles
